import {
  Button,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material'
import Notiflix from 'notiflix'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { useCustomNavigation } from '../../../../hooks/useCustomNavigate'
import instance from '../../../../providers/axios.config'
import { NavigationEnum } from '../../../../utils/constants'
import { CustomTextfield } from '../../../shared-components/textfield/CustomTextField'
import InputCode from '../../../shared-components/inputCode'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import styles from './ConfirmAccount.module.scss'

export const ConfirmAccount = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const [username, setUsername] = useState('')
  const [code, setCode] = useState('')
  const [errorFeedback, setErrorFeedback] = useState({ message: '', name: '' })
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const navigate = useCustomNavigation()

  // Pre-fill username from URL params if provided
  useEffect(() => {
    const usernameParam = searchParams.get('username')
    if (usernameParam) {
      setUsername(usernameParam)
    }
  }, [searchParams])

  const handleCodeComplete = (completedCode: string) => {
    setCode(completedCode)
  }

  const confirmAccount = async () => {
    setIsLoading(true)
    setErrorFeedback({ message: '', name: '' })

    try {
      // Validate required fields
      if (!username.trim()) {
        setErrorFeedback({
          message: t('Username is required'),
          name: 'username',
        })
        setIsLoading(false)
        return
      }

      if (!code || code.length !== 6) {
        setErrorFeedback({
          message: t('Please enter the complete 6-digit code'),
          name: 'code',
        })
        setIsLoading(false)
        return
      }

      await instance.post('auth/confirm-account', {
        username,
        code,
      })

      setIsSuccess(true)
      // Notiflix.Report.success(
      //   t('Account Confirmed'),
      //   t('Your account has been successfully confirmed. You can now log in.'),
      //   t('OK'),
      //   () => navigate(NavigationEnum.LOGIN)
      // )
    } catch (error: any) {
      console.error('Account confirmation failed:', error)

      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message
        
        setErrorFeedback({
          message: errorMessage,
          name: 'code',
        })
      } else {
        setErrorFeedback({
          message: t('An error occurred during confirmation. Please try again.'),
          name: 'apiError',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.successContainer}>
            <CheckCircleOutlineIcon className={styles.successIcon} />
            <Typography className={styles.successTitle}>
              {t('Account Confirmed!')}
            </Typography>
            <Typography className={styles.successMessage}>
              {t('Your account has been successfully verified.')}
            </Typography>
            <Button
              onClick={() => navigate(NavigationEnum.LOGIN)}
              className={styles.button}
            >
              {t('Go to Login')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.iconWrapper}>
          <LockOutlinedIcon className={styles.lockIcon} />
        </div>
        
        <Typography className={styles.welcome}>
          {t('Confirm Your Account')}
        </Typography>
        
        <Typography className={styles.subtitle}>
          {t('Enter your username and the 6-digit code sent to your email')}
        </Typography>

        <Box className={styles.formContainer}>
          <CustomTextfield
            variant='outlined'
            error={errorFeedback.name === 'username'}
            label={t('Username')}
            className={styles.textField}
            value={username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setUsername(e.target.value)
            }
          />

          <Box className={styles.codeSection}>
            <Typography className={styles.codeLabel}>
              {t('Confirmation Code')}
            </Typography>
            <InputCode
              length={6}
              name='confirmationCode'
              onGetCode={handleCodeComplete}
            />
            <Typography className={styles.codeHint}>
              {t('Enter the 6-digit code from your email')}
            </Typography>
          </Box>

          {errorFeedback.message && (
            <Typography variant='body2' className={styles.errorMessage}>
              {errorFeedback.message}
            </Typography>
          )}

          <Button
            onClick={!isLoading ? confirmAccount : undefined}
            className={styles.button}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              t('Confirm Account')
            )}
          </Button>

          <div className={styles.warningBox}>
            <Typography className={styles.warningText}>
              ⚠️ {t('If you did not create an account, please do not confirm and ignore this request.')}
            </Typography>
          </div>
        </Box>

        <div className={styles.links}>
          <Typography>
            {t('Already confirmed?')}{' '}
            <Link to={NavigationEnum.LOGIN} className={styles.linkText}>
              {t('Sign in')}
            </Link>
          </Typography>
          <Typography>
            {t("Don't have an account?")}{' '}
            <Link to={NavigationEnum.REGISTER} className={styles.linkText}>
              {t('Sign up')}
            </Link>
          </Typography>
        </div>
      </div>
    </div>
  )
}